import {
  addNewsArticle,
  deleteNewsArticle,
  getNewsArticleDetail,
  getNewsArticles,
  translateNewsArticle,
  updateNewsArticle,
  updateNewsArticleTranslation,
} from "@/services";
import { AddNewsArticleParams, NewsArticleParams, NewsArticleTranslateParams } from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";

const NEWS_ARTICLES_QUERY_KEY = ["news-articles"] as const;
const NEWS_ARTICLE_DETAIL_QUERY_KEY = ["news-article-detail"] as const;
const NEWS_ARTICLE_TRANSLATE_QUERY_KEY = ["news-article-translate"] as const;

export function useNewsArticles({
  offset = 0,
  limit = 10,
  category_id,
  status,
  search,
}: NewsArticleParams) {
  return useQuery({
    queryKey: [NEWS_ARTICLES_QUERY_KEY, offset, limit, category_id, status, search],
    queryFn: async () => {
      const response = await getNewsArticles({
        offset,
        limit,
        category_id,
        status,
        search,
      });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
  });
}

export function useNewsArticleDetail({ articleId }: { articleId: string }) {
  return useQuery({
    queryKey: [NEWS_ARTICLE_DETAIL_QUERY_KEY, articleId],
    queryFn: async () => {
      const response = await getNewsArticleDetail({ articleId });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!articleId,
  });
}

export function useTranslateNewsArticle({
  articleId,
  enabled = false,
  targetLang,
}: {
  articleId: string;
  enabled: boolean;
  targetLang: string;
}) {
  return useQuery({
    queryKey: [NEWS_ARTICLE_TRANSLATE_QUERY_KEY, articleId, targetLang],
    queryFn: async () => {
      const response = await translateNewsArticle({ articleId, targetLang });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: enabled && !!articleId,
  });
}

export function useAddNewsArticle() {
  return useMutation({
    mutationFn: async (params: AddNewsArticleParams) => addNewsArticle({ ...params }),
  });
}

export function useUpdateNewsArticle() {
  return useMutation({
    mutationFn: async ({
      articleId,
      params,
    }: {
      articleId: string;
      params: AddNewsArticleParams;
    }) => updateNewsArticle(articleId, params),
  });
}

export function useUpdateNewsArticleTranslation() {
  return useMutation({
    mutationFn: async ({
      articleId,
      params,
    }: {
      articleId: string;
      params: NewsArticleTranslateParams;
    }) => updateNewsArticleTranslation(articleId, params),
  });
}

export function useDeleteNewsArticle() {
  return useMutation({
    mutationFn: async ({ articleId }: { articleId: string }) => deleteNewsArticle(articleId),
  });
}
